import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Flame } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { smtpApi } from '../../api/smtp';
import type { SendingProfile } from '../../api/smtp';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';

interface SMTPFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** null creates a new profile; a profile edits it in place. */
  smtp: SendingProfile | null;
  initialPreset?: SMTPConnectionPreset;
}

export type SMTPConnectionPreset = 'custom' | 'postfix_submission' | 'postfix_private';

interface FormState {
  name: string;
  host: string;
  from_address: string;
  username: string;
  password: string;
  ignore_cert_errors: boolean;
  max_emails_per_hour: number;
  dkim_selector: string;
  warmup_enabled: boolean;
  warmup_start_date: string; // YYYY-MM-DD for <input type="date">
  warmup_days: number;
  warmup_target_daily: number;
}

const emptyForm: FormState = {
  name: '',
  host: '',
  from_address: '',
  username: '',
  password: '',
  ignore_cert_errors: false,
  max_emails_per_hour: 0,
  dkim_selector: '',
  warmup_enabled: false,
  warmup_start_date: '',
  warmup_days: 21,
  warmup_target_daily: 0,
};

function toFormState(smtp: SendingProfile | null): FormState {
  if (!smtp) return emptyForm;
  return {
    name: smtp.name,
    host: smtp.host,
    from_address: smtp.from_address,
    username: smtp.username ?? '',
    password: '', // never echoed back; blank keeps the stored password
    ignore_cert_errors: smtp.ignore_cert_errors,
    max_emails_per_hour: smtp.max_emails_per_hour ?? 0,
    dkim_selector: smtp.dkim_selector ?? '',
    warmup_enabled: smtp.warmup_enabled ?? false,
    warmup_start_date: smtp.warmup_start_date ? smtp.warmup_start_date.slice(0, 10) : '',
    warmup_days: smtp.warmup_days && smtp.warmup_days > 0 ? smtp.warmup_days : 21,
    warmup_target_daily: smtp.warmup_target_daily ?? 0,
  };
}

export function SMTPFormModal({ isOpen, onClose, smtp, initialPreset = 'custom' }: SMTPFormModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={smtp ? `Edit Sending Profile — ${smtp.name}` : 'New Sending Profile'}
      size="lg"
    >
      {/* The form remounts on every open (Modal renders nothing while
          closed), which resets field state to the target profile. */}
      <SMTPForm key={smtp?.id ?? 'new'} smtp={smtp} onClose={onClose} initialPreset={initialPreset} />
    </Modal>
  );
}

function SMTPForm({ smtp, onClose, initialPreset }: { smtp: SendingProfile | null; onClose: () => void; initialPreset: SMTPConnectionPreset }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => toFormState(smtp));
  const [error, setError] = useState<string | null>(null);
  const [preset, setPreset] = useState<SMTPConnectionPreset>(initialPreset);

  const changePreset = (next: SMTPConnectionPreset) => {
    setPreset(next);
    setError(null);
    if (next === 'custom') return;
    setForm(previous => ({
      ...previous,
      name: previous.name || 'Postfix relay',
      host: previous.host ? `${previous.host.replace(/:\d+$/, '')}:${next === 'postfix_submission' ? 587 : 25}` : '',
      ignore_cert_errors: false,
      ...(next === 'postfix_private' ? { username: '', password: '' } : {}),
    }));
  };

  const saveMutation = useMutation({
    mutationFn: (data: FormState) => {
      const payload: Parameters<typeof smtpApi.create>[0] = {
        name: data.name,
        host: data.host,
        from_address: data.from_address,
        username: data.username,
        ignore_cert_errors: data.ignore_cert_errors,
        max_emails_per_hour: data.max_emails_per_hour,
        dkim_selector: data.dkim_selector,
        warmup_enabled: data.warmup_enabled,
        warmup_days: data.warmup_enabled ? data.warmup_days : 0,
        warmup_target_daily: data.warmup_enabled ? data.warmup_target_daily : 0,
      };
      // Omit the field rather than sending "" — Go's time.Time rejects
      // empty strings during JSON decoding.
      if (data.warmup_enabled && data.warmup_start_date) {
        payload.warmup_start_date = `${data.warmup_start_date}T00:00:00Z`;
      }
      if (smtp) {
        // PUT replaces the whole profile: keep the id (required by the
        // handler) and the existing custom headers so they are not wiped.
        return smtpApi.update(smtp.id, {
          ...payload,
          id: smtp.id,
          headers: smtp.headers ?? [],
          ...(data.password ? { password: data.password } : {}),
        });
      }
      return smtpApi.create({
        ...payload,
        ...(data.password ? { password: data.password } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smtps'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to save sending profile');
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (preset === 'postfix_submission' && (!form.username.trim() || (!smtp && !form.password))) {
      setError('Enter the Postfix submission username and password.');
      return;
    }
    saveMutation.mutate(form);
  };

  return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="space-y-3 border-b border-gray-200 pb-5" aria-label="Outbound connection setup">
          <label className="label" htmlFor="smtp-connection-preset">Connection setup</label>
          <select id="smtp-connection-preset" className="input" value={preset} onChange={event => changePreset(event.target.value as SMTPConnectionPreset)}>
            <option value="custom">Custom SMTP server</option>
            <option value="postfix_submission">Postfix authenticated submission (587)</option>
            <option value="postfix_private">Postfix trusted private relay (25)</option>
          </select>
          {preset !== 'custom' && <div className="space-y-3 text-sm text-gray-600">
            <h3 className="font-semibold text-gray-900">Connect AwareNow to Postfix</h3>
            <p>{preset === 'postfix_submission' ? 'Use your Postfix hostname with port 587 and the submission account provided by your mail administrator. Enable STARTTLS and SASL authentication on that Postfix listener.' : 'Use port 25 only for a relay on your trusted private network. Postfix must permit only the AwareNow server address or tightly scoped trusted networks to relay.'}</p>
            <p>Use a From address your relay permits. Keep certificate verification enabled. Saving this profile configures AwareNow’s outbound connection; configure the Postfix server separately.</p>
            <nav aria-label="Postfix documentation" className="flex flex-wrap gap-x-4 gap-y-2 text-indigo-600">
              <a href="https://www.postfix.org/SASL_README.html" target="_blank" rel="noreferrer">Authentication setup</a>
              <a href="https://www.postfix.org/TLS_README.html" target="_blank" rel="noreferrer">TLS setup</a>
              <a href="https://www.postfix.org/SMTPD_ACCESS_README.html" target="_blank" rel="noreferrer">Relay access rules</a>
            </nav>
          </div>}
        </section>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label" htmlFor="smtp-name">Name</label>
              <input
                id="smtp-name"
                type="text"
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Primary relay"
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="smtp-host">SMTP Host</label>
              <input
                id="smtp-host"
                type="text"
                className="input"
                value={form.host}
                onChange={(e) => setForm({ ...form, host: e.target.value })}
                placeholder={preset === 'postfix_private' ? 'postfix.internal.example:25' : preset === 'postfix_submission' ? 'postfix.example.com:587' : 'smtp.example.com:587'}
                required
              />
              {preset !== 'custom' && <p className="text-sm text-gray-500 mt-1">Enter the hostname and port reachable from the AwareNow server.</p>}
            </div>
          </div>
          <div>
            <label className="label" htmlFor="smtp-from">From Address</label>
            <input
              id="smtp-from"
              type="email"
              className="input"
              value={form.from_address}
              onChange={(e) => setForm({ ...form, from_address: e.target.value })}
              placeholder="training@example.com"
              required
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label" htmlFor="smtp-username">Username</label>
              <input
                id="smtp-username"
                type="text"
                className="input"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                autoComplete="off"
                required={preset === 'postfix_submission'}
              />
            </div>
            <div>
              <label className="label" htmlFor="smtp-password">Password</label>
              <input
                id="smtp-password"
                type="password"
                className="input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={smtp ? 'Leave blank to keep current' : ''}
                autoComplete="new-password"
                required={preset === 'postfix_submission' && !smtp}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label" htmlFor="smtp-max-per-hour">Max emails per hour</label>
              <input
                id="smtp-max-per-hour"
                type="number"
                min={0}
                className="input"
                value={form.max_emails_per_hour}
                onChange={(e) => setForm({ ...form, max_emails_per_hour: Number(e.target.value) })}
              />
              <p className="text-sm text-gray-500 mt-1">0 = unlimited</p>
            </div>
            <div>
              <label className="label" htmlFor="smtp-dkim-selector">DKIM selector</label>
              <input
                id="smtp-dkim-selector"
                type="text"
                className="input"
                value={form.dkim_selector}
                onChange={(e) => setForm({ ...form, dkim_selector: e.target.value })}
                placeholder="selector1"
              />
              <p className="text-sm text-gray-500 mt-1">
                Used by preflight to check <code>&lt;selector&gt;._domainkey.&lt;domain&gt;</code>
              </p>
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.ignore_cert_errors}
              onChange={(e) => setForm({ ...form, ignore_cert_errors: e.target.checked })}
              className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
            />
            <div>
              <p className="font-medium text-gray-900">Ignore certificate errors</p>
              <p className="text-sm text-gray-500">Allow self-signed or mismatched TLS certificates</p>
            </div>
          </label>
        </div>

        <div className="border-t pt-4 space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.warmup_enabled}
              onChange={(e) => setForm({ ...form, warmup_enabled: e.target.checked })}
              className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
            />
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              <div>
                <p className="font-medium text-gray-900">IP/domain warmup</p>
                <p className="text-sm text-gray-500">
                  Ramp daily volume linearly from 5/day up to the target — protects cold sending
                  infrastructure from being flagged
                </p>
              </div>
            </div>
          </label>
          {form.warmup_enabled && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 rounded-lg bg-orange-50 p-4">
              <div>
                <label className="label" htmlFor="smtp-warmup-start">Start date</label>
                <input
                  id="smtp-warmup-start"
                  type="date"
                  className="input"
                  value={form.warmup_start_date}
                  onChange={(e) => setForm({ ...form, warmup_start_date: e.target.value })}
                />
              </div>
              <div>
                <label className="label" htmlFor="smtp-warmup-days">Ramp days</label>
                <input
                  id="smtp-warmup-days"
                  type="number"
                  min={1}
                  className="input"
                  value={form.warmup_days}
                  onChange={(e) => setForm({ ...form, warmup_days: Number(e.target.value) })}
                />
              </div>
              <div>
                <label className="label" htmlFor="smtp-warmup-target">Target per day</label>
                <input
                  id="smtp-warmup-target"
                  type="number"
                  min={0}
                  className="input"
                  value={form.warmup_target_daily}
                  onChange={(e) => setForm({ ...form, warmup_target_daily: Number(e.target.value) })}
                />
                <p className="text-sm text-gray-500 mt-1">0 = no daily cap</p>
              </div>
            </div>
          )}
        </div>

        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        {saveMutation.isError && !error && (
          <p className="text-sm text-red-600">Failed to save sending profile.</p>
        )}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saveMutation.isPending}>
            {smtp ? 'Save Changes' : 'Create Profile'}
          </Button>
        </div>
      </form>
  );
}
